import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
    Wallet, ChevronRight, RefreshCw, Download, Loader2, AlertTriangle, Filter, X, Eye, Search,
} from 'lucide-react';

import {
    fetchOutstandingClients, fetchOutstandingSubClients, fetchClientOutstandingReport, fetchClientOutstandingDetail,
    clearSubClients, clearClientOutstandingReport, clearClientOutstandingDetail, resetClientOutstandingReport,
    selectCORClientList, selectCORClientListLoading,
    selectCORSubClientList, selectCORSubClientListLoading,
    selectCORReport, selectCORReportLoading, selectCORReportError,
    selectCORDetail, selectCORDetailLoading,
} from '../../slices/ReportsSlice/clientOutstandingReportSlice';

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent';

const SELECT_ALL_CLIENT = 'All';
const SELECT_ALL_SUBCLIENT = 'All';

const num = (v) => parseFloat(v || 0) || 0;
const money = (v) => num(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// spGetAllOutstandingclients' Clientname column is `ClientName + ' , ' + ClientCode`
// (confirmed in the SQL script) — not a display name on its own. Same pattern for
// spGetSubClientsbyClientOSReport's SubClientCodename (`BranchName + ' ,' + SubClientCode`).
const cleanName = (raw) => (raw || '').split(',')[0].trim();

const DetailModal = ({ rows, loading, clientLabel, onClose }) => {
    const totals = useMemo(() => rows.reduce((acc, r) => {
        acc.InvoiceBalance += num(r.InvoiceBalance);
        acc.RetentionBalance += num(r.RetentionBalance);
        acc.HoldBalance += num(r.HoldBalance);
        return acc;
    }, { InvoiceBalance: 0, RetentionBalance: 0, HoldBalance: 0 }), [rows]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-2xl sticky top-0">
                    <h2 className="text-sm font-bold text-white">Invoice Detail — {clientLabel}</h2>
                    <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4 text-white" /></button>
                </div>
                <div className="p-4">
                    {loading ? (
                        <div className="flex items-center justify-center gap-3 py-10"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /><span className="text-sm text-gray-500">Loading…</span></div>
                    ) : rows.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-8">No outstanding invoices found.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-[11px] divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-100 dark:bg-gray-900/40">
                                    <tr>
                                        {['CC Code', 'Invoice Date', 'Invoice No', 'RA No', 'Invoice Balance', 'Retention Balance', 'Hold Balance', 'Invoice Age'].map((h) => (
                                            <th key={h} className="px-2 py-1.5 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {rows.map((r, i) => (
                                        <tr key={i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10">
                                            <td className="px-2 py-1 whitespace-nowrap">{r.CCCode}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{r.InvoiceDate}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{r.InvoiceNo}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{r.RANumber}</td>
                                            <td className="px-2 py-1 text-right">{money(r.InvoiceBalance)}</td>
                                            <td className="px-2 py-1 text-right">{money(r.RetentionBalance)}</td>
                                            <td className="px-2 py-1 text-right">{money(r.HoldBalance)}</td>
                                            <td className="px-2 py-1 text-right">{r.InvoiceAgeinnumbers}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                        <td className="px-2 py-1" colSpan={4}>TOTAL</td>
                                        <td className="px-2 py-1 text-right">{money(totals.InvoiceBalance)}</td>
                                        <td className="px-2 py-1 text-right">{money(totals.RetentionBalance)}</td>
                                        <td className="px-2 py-1 text-right">{money(totals.HoldBalance)}</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ClientOutstandingReport = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const uid = userData?.UID || userData?.uid;
    const roleId = userData?.roleId || userData?.RID;

    const clientList = useSelector(selectCORClientList);
    const clientListLoading = useSelector(selectCORClientListLoading);
    const subClientListRaw = useSelector(selectCORSubClientList);
    const subClientListLoading = useSelector(selectCORSubClientListLoading);
    const report = useSelector(selectCORReport);
    const reportLoading = useSelector(selectCORReportLoading);
    const reportError = useSelector(selectCORReportError);
    const detail = useSelector(selectCORDetail);
    const detailLoading = useSelector(selectCORDetailLoading);

    const [clientCode, setClientCode] = useState('');
    const [subClientCode, setSubClientCode] = useState('');
    const [ranParams, setRanParams] = useState(null);
    const [detailRow, setDetailRow] = useState(null);

    const clientOptions = useMemo(() => clientList.map((c) => ({ ...c, Clientname: cleanName(c.Clientname) })), [clientList]);
    const subClientOptions = useMemo(() => subClientListRaw.map((c) => ({ ...c, SubClientCodename: cleanName(c.SubClientCodename) })), [subClientListRaw]);

    const needsSubClient = clientCode && clientCode !== SELECT_ALL_CLIENT;

    useEffect(() => {
        dispatch(fetchOutstandingClients());
        return () => dispatch(resetClientOutstandingReport());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClientChange = (val) => {
        setClientCode(val);
        setSubClientCode('');
        dispatch(clearSubClients());
        dispatch(clearClientOutstandingReport());
        if (val && val !== SELECT_ALL_CLIENT) {
            dispatch(fetchOutstandingSubClients(val));
        }
    };

    const handleReset = () => {
        setClientCode(''); setSubClientCode(''); setRanParams(null);
        dispatch(resetClientOutstandingReport());
        dispatch(fetchOutstandingClients());
    };

    const runReport = () => {
        if (!clientCode) { toast.warning('Select Client'); return; }
        if (needsSubClient && !subClientCode) { toast.warning('Select Sub-Client'); return; }

        const params = { UID: uid, Roleid: roleId, ClientCode: clientCode, SubClientCode: needsSubClient ? subClientCode : '' };
        setRanParams(params);
        dispatch(fetchClientOutstandingReport(params));
    };

    const handleRefresh = () => { if (ranParams) dispatch(fetchClientOutstandingReport(ranParams)); };

    const totals = useMemo(() => report.reduce((acc, r) => {
        acc.InvoiceBalBelow += num(r.InvoiceBalBelow);
        acc.InvoiceBalBetween91to120 += num(r.InvoiceBalBetween91to120);
        acc.InvoiceBalBetween121to180 += num(r.InvoiceBalBetween121to180);
        acc.InvoiceBalAbove += num(r.InvoiceBalAbove);
        acc.RetentionBalance += num(r.RetentionBalance);
        acc.HoldBalance += num(r.HoldBalance);
        acc.TotalBalance += num(r.TotalBalance);
        return acc;
    }, { InvoiceBalBelow: 0, InvoiceBalBetween91to120: 0, InvoiceBalBetween121to180: 0, InvoiceBalAbove: 0, RetentionBalance: 0, HoldBalance: 0, TotalBalance: 0 }), [report]);

    const openDetail = (row) => {
        setDetailRow(row);
        dispatch(fetchClientOutstandingDetail({ Clientcode: row.ClientCode, SubClientCode: row.SubClientCode, UID: uid, Roleid: roleId }));
    };
    const closeDetail = () => { setDetailRow(null); dispatch(clearClientOutstandingDetail()); };

    const handleExport = () => {
        if (report.length === 0) { toast.warning('No data to export'); return; }
        const exportRows = report.map((r) => ({
            'Client Code': r.ClientCode, 'Client Name': r.ClientName, 'SubClient Code': r.SubClientCode, 'SubClient Name': r.SubClientName,
            Location: r.Location,
            'Below 90 Days': r.InvoiceBalBelow, 'Between 91-120 Days': r.InvoiceBalBetween91to120, 'Between 121-180 Days': r.InvoiceBalBetween121to180, 'Above 180 Days': r.InvoiceBalAbove,
            'Retention Balance': r.RetentionBalance, 'Hold Balance': r.HoldBalance, 'Total Balance': r.TotalBalance,
        }));
        exportRows.push({ 'Client Code': '', 'Client Name': 'TOTAL', ...totals });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportRows), 'Client Outstanding');
        XLSX.writeFile(wb, `Client_Outstanding_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Excel downloaded');
    };

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <Wallet className="h-4 w-4 text-white" />
                            </div>
                            Client Current Outstanding Report
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Ageing of client invoice, retention and hold balances</p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">Client Outstanding Report</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={reportLoading || !ranParams} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <RefreshCw className={`h-3.5 w-3.5 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                        </button>
                        <button onClick={handleExport} disabled={report.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <Download className="h-3.5 w-3.5" />Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-2">
                    <Filter className="h-3.5 w-3.5 text-indigo-600" />
                    <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Client</label>
                        <select className={inputCls} value={clientCode} onChange={(e) => handleClientChange(e.target.value)} disabled={clientListLoading}>
                            <option value="">-Please Select-</option>
                            {clientOptions.map((c) => <option key={c.Clientcode} value={c.Clientcode}>{c.Clientname}</option>)}
                        </select>
                    </div>
                    {needsSubClient && (
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Sub-Client</label>
                            <select className={inputCls} value={subClientCode} onChange={(e) => setSubClientCode(e.target.value)} disabled={subClientListLoading}>
                                <option value="">-Please Select-</option>
                                <option value={SELECT_ALL_SUBCLIENT}>Select All</option>
                                {subClientOptions.map((c) => <option key={c.SubClientId} value={c.SubClientCode}>{c.SubClientCodename}</option>)}
                            </select>
                        </div>
                    )}
                    <div className="flex gap-2">
                        <button onClick={runReport} disabled={reportLoading} className="flex-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5">
                            <Search className="w-3.5 h-3.5" /> View
                        </button>
                        <button onClick={handleReset} className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors">Reset</button>
                    </div>
                </div>
            </div>

            {/* Report */}
            {reportLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading report…</span>
                </div>
            ) : reportError ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{reportError}</span>
                </div>
            ) : ranParams && report.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No Data Found.</div>
            ) : report.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">Client Outstanding Summary</h2>
                    </div>
                    <div className="overflow-auto max-h-[70vh]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                <tr>
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase">Detail</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">Client Code</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">Client Name</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">SubClient Code</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">SubClient Name</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">Location</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Below 90 Days</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">91-120 Days</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">121-180 Days</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Above 180 Days</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Retention Bal</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Hold Bal</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Total Balance</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {report.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-2 py-1 text-center">
                                            <button onClick={() => openDetail(row)} className="inline-flex text-indigo-600 hover:text-indigo-800">
                                                <Eye className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                        <td className="px-2 py-1 text-xs whitespace-nowrap">{row.ClientCode}</td>
                                        <td className="px-2 py-1 text-xs text-gray-800 dark:text-gray-100 whitespace-nowrap">{row.ClientName}</td>
                                        <td className="px-2 py-1 text-xs whitespace-nowrap">{row.SubClientCode}</td>
                                        <td className="px-2 py-1 text-xs whitespace-nowrap">{row.SubClientName}</td>
                                        <td className="px-2 py-1 text-xs whitespace-nowrap">{row.Location}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.InvoiceBalBelow)}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.InvoiceBalBetween91to120)}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.InvoiceBalBetween121to180)}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.InvoiceBalAbove)}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.RetentionBalance)}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.HoldBalance)}</td>
                                        <td className="px-2 py-1 text-xs text-right font-semibold whitespace-nowrap">{money(row.TotalBalance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                    <td className="px-2 py-1 text-xs" colSpan={6}>TOTAL</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.InvoiceBalBelow)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.InvoiceBalBetween91to120)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.InvoiceBalBetween121to180)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.InvoiceBalAbove)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.RetentionBalance)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.HoldBalance)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.TotalBalance)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}

            {detailRow && (
                <DetailModal rows={detail} loading={detailLoading}
                    clientLabel={`${detailRow.ClientName || detailRow.ClientCode}${detailRow.SubClientName ? ' / ' + detailRow.SubClientName : ''}`}
                    onClose={closeDetail} />
            )}
        </div>
    );
};

export default ClientOutstandingReport;
